import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Minus, Plus, ShoppingBag, Heart, Truck, Shield, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ProductCard from '../components/Products/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
      
      // Fetch related products from same category
      const relatedResponse = await axios.get(`${API}/products?category=${response.data.category}`);
      setRelatedProducts(
        relatedResponse.data.filter(p => p.id !== productId).slice(0, 4)
      );
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Produit non trouvé');
      navigate('/products');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      toast.success(`${product.name} ajouté au panier`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen section-cream" data-testid="product-detail-page">
      <div className="container-custom py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        {/* Product Detail */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Product Image */}
          <div className="relative">
            <div className="aspect-square overflow-hidden bg-surface">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>
            {product.featured && (
              <span className="absolute top-4 left-4 bg-accent text-white text-xs uppercase tracking-widest px-3 py-1">
                Coup de cœur
              </span>
            )}
          </div>

          {/* Product Info */}
          <div className="lg:py-8">
            {/* Category */}
            <span className="text-accent uppercase tracking-widest text-sm font-medium mb-4 block">
              {product.category === 'brumes' && 'Brumes Corporelles'}
              {product.category === 'cremes' && 'Crèmes pour la Peau'}
              {product.category === 'musc' && 'Musc & Parfums'}
              {product.category === 'barbes' && 'Produits Barbes'}
            </span>

            {/* Title */}
            <h1 
              className="font-serif text-3xl md:text-4xl text-text-primary mb-4"
              data-testid="product-name"
            >
              {product.name}
            </h1>

            {/* Fragrance & Volume */}
            <p className="text-text-secondary mb-4">
              {product.fragrance} • {product.volume}
            </p>

            {/* Price */}
            <p 
              className="text-2xl text-accent font-serif mb-6"
              data-testid="product-price"
            >
              {product.price.toFixed(2)} €
            </p>

            {/* Description */}
            <p 
              className="text-text-secondary leading-relaxed mb-8"
              data-testid="product-description"
            >
              {product.description}
            </p>

            {/* Quantity Selector */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-text-secondary text-sm uppercase tracking-wide">Quantité</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="quantity-btn"
                  data-testid="decrease-quantity"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span 
                  className="w-12 text-center font-medium"
                  data-testid="quantity-value"
                >
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="quantity-btn"
                  data-testid="increase-quantity"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                onClick={handleAddToCart}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag className="w-4 h-4" />
                Ajouter au panier
              </Button>
              <Button
                variant="outline"
                className="btn-outline flex items-center justify-center gap-2"
                data-testid="wishlist-btn"
              >
                <Heart className="w-4 h-4" />
                Favoris
              </Button>
            </div>

            {/* Benefits */}
            <div className="border-t border-border pt-8 space-y-4">
              <div className="flex items-center gap-3 text-text-secondary">
                <Truck className="w-5 h-5 text-accent" />
                <span className="text-sm">Livraison offerte dès 50€ d'achat</span>
              </div>
              <div className="flex items-center gap-3 text-text-secondary">
                <Shield className="w-5 h-5 text-accent" />
                <span className="text-sm">Paiement 100% sécurisé</span>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-24">
            <h2 className="font-serif text-2xl md:text-3xl text-text-primary mb-8">
              Vous aimerez aussi
            </h2>
            <div className="product-grid">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
